import React, { useContext, useEffect, useState } from 'react';
import { SessionContext } from '../../providers/SessionProvider';
import moment from 'moment';
import 'moment/locale/pt';
import { Link } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import { useSession } from 'src/app/hooks/useSession';
import { FeedCommentAnswers } from './feed-comment-answers';
import toast from 'react-hot-toast';
import { PostActionsButton, PostActionsItem, PostActionsWrapper, PostComment, PostCommentBody, PostCommentContent, PostCommentHeader, PostCommentName, PostCommentText, PostCommentTime } from '@/components/custom/post';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FiTrash2 } from 'react-icons/fi';
import { Loader } from '@/components/custom/loader';

export function FeedComment({ comment, pinned, comments, setPinnedComment, hidePinnedBtn, className, deleteCommentFromParent, setPost, ...props }) {
  const client = useClient();
  const state = useContext(SessionContext);
  const { session } = useSession();
  const isAdmin = session.user.profileNames.includes('Administrador');
  const [isOpenAnswer, setIsOpenAnswer] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const findAnswers = () => {
    const commentsWithAnswers = comments?.filter(comment => comment?.['comment-parent-id'] !== null);
    const answersByCommentId = commentsWithAnswers?.filter(item => item?.['comment-parent-id'] === comment?.id).reverse();

    setAnswers(answersByCommentId);
  };

  const handleCreateAnswer = async value => {
    try {
      const response = await client.mutate('/comments', {
        'comment-parent-id': comment?.id,
        body: value,
        'post-id': comment['post-id']
      });

      setAnswers(prev => [response.data, ...prev]);
    } catch (error) {
      console.log(error);
      toast.error('Erro ao responder');
    }
  };

  const handleDeleteAnswer = async answerId => {
    setIsDeleting(true);

    try {
      const { error } = await client.delete(`/comments/${answerId}`);

      if (error) {
        console.error('Erro de API no payload:', error);
        toast.error('Erro ao deletar resposta');
        return;
      }

      setAnswers(prev => prev.filter(item => item.id !== answerId));

      if (setPost && comments) {
        const postToUpdate = { comments: [...comments] };
        postToUpdate.comments = postToUpdate.comments.filter(c => c.id !== answerId);
        setPost(prev => ({ ...prev, comments: postToUpdate.comments }));
      }
    } catch (error) {
      console.error('Erro de rede/execução:', error);
      toast.error('Erro ao deletar resposta');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMainCommentDelete = () => {
    if (deleteCommentFromParent) {
      deleteCommentFromParent(comment, setIsDeleting);
    }
  };

  useEffect(() => {
    findAnswers();
  }, [comments]);

  return (
    <PostComment
      isPinned={pinned}
      className={className}
      {...props}
    >
      {isDeleting && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70">
          <Loader />
        </div>
      )}

      {pinned ? (
        <Badge
          variant="bright"
          size="xs"
          square
          className="font-light"
        >
          Comentário fixado
        </Badge>
      ) : (
        isAdmin &&
        !hidePinnedBtn && (
          <Button
            variant="outline"
            size="xs"
            onClick={() => setPinnedComment(comment?.id)}
            disabled={isDeleting}
          >
            Fixar comentário
          </Button>
        )
      )}

      <Link
        to={`/usuario/metricas/${comment?.user?.id}`}
        state={{ isRedirected: true }}
      >
        <AvatarWrapper
          size="sm"
          src={comment?.user?.image?.url}
          text={comment?.user?.name}
        />
      </Link>

      <PostCommentContent>
        <PostCommentHeader>
          <PostCommentName>
            <Link
              to={`/usuario/metricas/${comment?.user?.id}`}
              state={{ isRedirected: true }}
            >
              {comment?.user?.name}
            </Link>
          </PostCommentName>
          <PostCommentTime>{moment(comment['created_at'] || comment['created-at']).fromNow()}</PostCommentTime>
        </PostCommentHeader>

        <PostCommentBody>
          <PostCommentText>{comment.body}</PostCommentText>
        </PostCommentBody>

        <FeedCommentAnswers
          answers={answers}
          onSubmit={handleCreateAnswer}
          isOpenAnswer={isOpenAnswer}
          onDelete={handleDeleteAnswer}
        />
      </PostCommentContent>

      <div className="flex flex-col gap-2 pt-8">
        <Button
          variant="link"
          size="xs"
          style={{ opacity: isOpenAnswer ? 0.5 : 1 }}
          onClick={() => setIsOpenAnswer(prev => !prev)}
          disabled={isDeleting}
        >
          Responder
        </Button>

        {state.session && state.session.user?.id === comment?.user?.id && (
          <PostActionsWrapper className="w-full flex-row">
            <PostActionsItem className="flex grow flex-row justify-end">
              <PostActionsButton
                isDanger
                onClick={handleMainCommentDelete}
                className="mr-0"
                disabled={isDeleting}
              >
                {isDeleting ? <Loader /> : <FiTrash2 size={16} />}
              </PostActionsButton>
            </PostActionsItem>
          </PostActionsWrapper>
        )}
      </div>
    </PostComment>
  );
}
